import type { ReactNode } from "jsx-dom";
import { Logger } from "@fruugo/utilities";
import { getTranslations } from "./api";

const Log = new Logger(window.environment);

const regex = /marketplace\/i18n\/messages_([a-z]{2})-(.*)\.properties/;

const placeholderRegex = /\{(\d+)\}/g;

// Split logging out so we can detect when there's a race condition vs failed endpoint
const failedLanguages = new Set<string>();

/** Convert the contents of an i18n properties file into a key/value object */
function parsePropertiesFile(contents: string): Record<string, string> {
  const object: Record<string, string> = {};

  contents.split("\n").forEach((line) => {
    if (line.trim() === "") return; // ignore blank lines
    if (line[0] === "#") return; // ignore comments
    const [key, ...rest] = line.split("=");
    const value = rest.join("=");
    object[key.trim()] = value.trim();
  });

  return object;
}

/** Convert a template string, i.e "Hello {0}", ["bob"] => "Hello Bob" */
function substitute(string: string, params: string[] = []) {
  let str = string;
  params.forEach((param, i) => {
    str = str.replace("{" + i + "}", param);
  });
  return str;
}

function substituteRich(template: string, params: ReactNode[] = []): ReactNode {
  if (params.length === 0) {
    return template;
  }

  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  template.replace(
    placeholderRegex,
    (match: string, index: string, offset: number) => {
      if (offset > lastIndex) {
        nodes.push(template.slice(lastIndex, offset));
      }

      const paramIndex = Number(index);
      nodes.push(params[paramIndex] ?? "");

      lastIndex = offset + match.length;
      return match;
    }
  );

  if (lastIndex < template.length) {
    nodes.push(template.slice(lastIndex));
  }

  const result = nodes.length === 1 ? nodes[0] : nodes;
  return result as ReactNode;
}

// any in flight requests for message files
const inFlight: Record<string, Promise<Record<string, string>> | undefined> =
  {};

// in memory cache of translations
const cache: Record<string, Record<string, string>> = {};

/** pre-warm the i18n cache by pulling the keys for a given language out of storage or across the network */
function initTranslation(resourceUrl = window.messagePropertiesResourceUrl) {
  const [_, language, newVersion] = resourceUrl.match(regex) || [];

  const storageKey = `i18n_${language}`;
  const versionKey = `i18n_${language}_version`;

  const cachedVersion = sessionStorage.getItem(versionKey);

  if (newVersion === cachedVersion) {
    const translations = sessionStorage.getItem(storageKey);

    if (translations !== null) {
      const parsed = JSON.parse(translations);
      cache[language] = parsed;
      return Promise.resolve(parsed);
    }
  }

  if (inFlight[language]) {
    // optimisation - skip loading the same language again if it's already in transit
    return inFlight[language];
  }

  const promise: Promise<Record<string, string>> = getTranslations(resourceUrl)
    .then(parsePropertiesFile)
    .then((translations) => {
      sessionStorage.setItem(storageKey, JSON.stringify(translations));
      sessionStorage.setItem(versionKey, newVersion);
      cache[language] = translations;
      return translations;
    })
    .catch((err) => {
      Log.error(err);
      failedLanguages.add(language);
      return {};
    })
    .finally(() => {
      inFlight[language] = undefined;
    });

  inFlight[language] = promise;

  return promise;
}

function resolveTranslation(key: string, language: string): string | null {
  if (failedLanguages.has(language)) {
    return null;
  }

  const translations = cache[language];

  if (!translations) {
    if (inFlight[language]) {
      Log.warning(
        `translations are still loading for language: "${language}", unable to translate key "${key}"`
      );
    } else {
      Log.warning(
        `translations for language "${language}" not loaded into cache, unable to translate key "${key}"`
      );
    }
    return null;
  }

  const translation = translations[key];

  if (!translation) {
    Log.warning(
      `no translation found for language "${language}" and key "${key}"`
    );
    return null;
  }

  return translation;
}

function i18n(
  key: string,
  params: string | string[] = [],
  language = window.currentLang
): string {
  const translation = resolveTranslation(key, language);

  if (translation === null) {
    return key;
  }

  const paramArray = Array.isArray(params) ? params : [params];

  return substitute(translation, paramArray);
}

function i18nRich(
  key: string,
  params: ReactNode | ReactNode[] = [],
  language = window.currentLang
): ReactNode {
  const translation = resolveTranslation(key, language);

  if (translation === null) {
    return key;
  }

  const paramArray = (Array.isArray(params) ? params : [params]).map((param) =>
    param == null || param === false ? "" : param
  );

  return substituteRich(translation, paramArray);
}

function waitForTranslations(
  language = window.currentLang
): Promise<Record<string, string>> {
  if (cache[language]) return Promise.resolve({});

  const promise = inFlight[language];
  if (promise) return promise;
  return Promise.reject("no translations loaded within time limit");
}

export { initTranslation, i18n, i18nRich, waitForTranslations };
